import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useMemo } from 'react';
import { meApi, spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import { useAuth } from '../store/auth-context';
import { useMeSpaces } from './useMeSpaces';
import { resolveDefaultSpaceId } from '../lib/default-space';
import type {
  Conversation,
  PaginatedResponse,
  UpdateConversationInput,
  CreateConversationInput,
} from '../types';

/**
 * The "default" space for operator-side conversation create/update/delete:
 * the user's Personal Space, falling back to last-visited or first available.
 * `/me` does not currently return `defaultSpaceId`, so we resolve it from
 * the spaces list (where each Space carries `kind: 'personal' | 'shared'`).
 */
function useDefaultConversationSpaceId(): string | undefined {
  const { user } = useAuth();
  const { data } = useMeSpaces();
  return useMemo(
    () =>
      resolveDefaultSpaceId({
        spaces: data?.items ?? [],
        lastSpaceId: user?.preferences?.lastSpaceId,
        defaultSpaceId: user?.defaultSpaceId,
      }),
    [data?.items, user?.preferences?.lastSpaceId, user?.defaultSpaceId],
  );
}

// ---------------------------------------------------------------------------
// Cross-space — used by operator surfaces (ChatList, OperatorSidebar) and
// the navbar dropdown. Reads every persistent user-origin conversation across
// all spaces the user can access.
// ---------------------------------------------------------------------------

// Exported for cache-patching event bridges (useConversationNamedEvent) —
// duplicating these literals elsewhere is how the original named-event hook
// silently drifted onto dead keys after the last restructure.
export const ME_LIST_KEY = ['me', 'conversations', 'list', { origin: 'user' }] as const;
export const ME_INFINITE_KEY = ['me', 'conversations', 'infinite', { origin: 'user' }] as const;
export const ME_INVALIDATE_PREFIX = ['me', 'conversations'] as const;

export function useMeConversations() {
  return useQuery({
    queryKey: ME_LIST_KEY,
    queryFn: () => meApi.conversations({ persistent: true, origin: 'user' }),
  });
}

export function useInfiniteMeConversations(pageSize = 30) {
  const query = useInfiniteQuery({
    queryKey: ME_INFINITE_KEY,
    queryFn: ({ pageParam }) =>
      meApi.conversations({
        persistent: true,
        origin: 'user',
        limit: pageSize,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextToken,
  });

  const items = useMemo<Conversation[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  // The complete pinned set ships only on the first page; later pages omit it.
  const pinned = useMemo<Conversation[]>(
    () => query.data?.pages[0]?.pinned ?? [],
    [query.data],
  );

  return {
    items,
    pinned,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// Space-scoped — used by the console ConversationList page.
// ---------------------------------------------------------------------------

function useConversationApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).conversations, [spaceId]);
}

export function useSpaceConversations() {
  const spaceId = useSpaceId();
  const api = useConversationApi();
  return useQuery({
    queryKey: ['space', spaceId, 'conversations', 'list', { origin: 'user' }],
    queryFn: () => api.list({ persistent: true, origin: 'user' }),
  });
}

export function useSpaceConversation(conversationId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useConversationApi();
  return useQuery({
    queryKey: ['space', spaceId, 'conversations', conversationId],
    queryFn: () => api.getConversation(conversationId!),
    enabled: !!conversationId,
  });
}

// ---------------------------------------------------------------------------
// Mutations — accept an explicit spaceId so callers can target the current
// space (console) or the user's default space (operator/Builder).
// ---------------------------------------------------------------------------

export function useCreateConversationIn(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateConversationInput) => {
      if (!spaceId) throw new Error('spaceId required to create conversation');
      return spaceApi(spaceId).conversations.create(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ME_INVALIDATE_PREFIX });
      if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'conversations'] });
      }
    },
  });
}

export function useUpdateConversationIn(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      input,
    }: {
      conversationId: string;
      input: UpdateConversationInput;
    }) => {
      if (!spaceId) throw new Error('spaceId required to update conversation');
      return spaceApi(spaceId).conversations.update(conversationId, input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ME_INVALIDATE_PREFIX });
      if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'conversations'] });
      }
    },
  });
}

type ConversationListPage = PaginatedResponse<Conversation> & {
  pinned: Conversation[];
};

interface InfiniteSnapshot {
  pages: ConversationListPage[];
  pageParams: unknown[];
}

const byRecent = (a: Conversation, b: Conversation) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

/**
 * Move a conversation between the unpinned `items` list and the `pinned` array
 * of a single first-page snapshot, flipping its `pinned` flag. Used for the
 * optimistic pin/unpin cache patch. `items` from non-first pages are handled
 * separately by the infinite patcher below.
 */
function applyPinToPage(
  page: ConversationListPage,
  conversationId: string,
  pinned: boolean,
): ConversationListPage {
  const all = [...page.items, ...page.pinned];
  const target = all.find((c) => c.conversationId === conversationId);
  if (!target) return page;
  const patched = { ...target, pinned, persistent: pinned ? true : target.persistent };
  const items = page.items.filter((c) => c.conversationId !== conversationId);
  const pinnedList = page.pinned.filter((c) => c.conversationId !== conversationId);
  if (pinned) {
    pinnedList.unshift(patched);
    pinnedList.sort(byRecent);
  } else {
    items.unshift(patched);
    items.sort(byRecent);
  }
  return { ...page, items, pinned: pinnedList };
}

/**
 * Toggle the pinned state of a conversation. Pinning forces `persistent: true`
 * server-side and moves the row into the always-complete `pinned` array; we
 * mirror that optimistically across the list + infinite caches.
 */
export function useTogglePinConversationIn(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      conversationId,
      pinned,
    }: {
      conversationId: string;
      pinned: boolean;
    }) => {
      if (!spaceId) throw new Error('spaceId required to pin conversation');
      return spaceApi(spaceId).conversations.update(conversationId, { pinned });
    },
    onMutate: async ({ conversationId, pinned }) => {
      await queryClient.cancelQueries({ queryKey: ME_INVALIDATE_PREFIX });

      const previousList =
        queryClient.getQueryData<ConversationListPage>(ME_LIST_KEY);
      if (previousList) {
        queryClient.setQueryData<ConversationListPage>(
          ME_LIST_KEY,
          applyPinToPage(previousList, conversationId, pinned),
        );
      }

      const previousInfinite =
        queryClient.getQueryData<InfiniteSnapshot>(ME_INFINITE_KEY);
      if (previousInfinite) {
        const target = previousInfinite.pages
          .flatMap((p) => [...p.items, ...p.pinned])
          .find((c) => c.conversationId === conversationId);
        queryClient.setQueryData<InfiniteSnapshot>(ME_INFINITE_KEY, {
          ...previousInfinite,
          pages: previousInfinite.pages.map((p, i) => {
            // Strip the conversation from every page's items, then re-home it
            // on the first page (which owns the canonical `pinned` array).
            const items = p.items.filter((c) => c.conversationId !== conversationId);
            if (i !== 0) return { ...p, items };
            const first: ConversationListPage = {
              ...p,
              items,
              pinned: p.pinned.filter((c) => c.conversationId !== conversationId),
            };
            if (!target) return first;
            const patched = {
              ...target,
              pinned,
              persistent: pinned ? true : target.persistent,
            };
            if (pinned) {
              first.pinned = [patched, ...first.pinned].sort(byRecent);
            } else {
              first.items = [patched, ...first.items].sort(byRecent);
            }
            return first;
          }),
        });
      }

      return { previousList, previousInfinite };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(ME_LIST_KEY, context.previousList);
      }
      if (context?.previousInfinite) {
        queryClient.setQueryData(ME_INFINITE_KEY, context.previousInfinite);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ME_INVALIDATE_PREFIX });
      if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'conversations'] });
      }
    },
  });
}

export function useDeleteConversationIn(spaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => {
      if (!spaceId) throw new Error('spaceId required to delete conversation');
      return spaceApi(spaceId).conversations.delete(conversationId);
    },
    onMutate: async (conversationId) => {
      await queryClient.cancelQueries({ queryKey: ME_INVALIDATE_PREFIX });

      const previousList =
        queryClient.getQueryData<PaginatedResponse<Conversation>>(ME_LIST_KEY);
      if (previousList) {
        queryClient.setQueryData<PaginatedResponse<Conversation>>(ME_LIST_KEY, {
          ...previousList,
          items: previousList.items.filter((c) => c.conversationId !== conversationId),
        });
      }

      const previousInfinite =
        queryClient.getQueryData<InfiniteSnapshot>(ME_INFINITE_KEY);
      if (previousInfinite) {
        queryClient.setQueryData<InfiniteSnapshot>(ME_INFINITE_KEY, {
          ...previousInfinite,
          pages: previousInfinite.pages.map((p) => ({
            ...p,
            items: p.items.filter((c) => c.conversationId !== conversationId),
          })),
        });
      }

      return { previousList, previousInfinite };
    },
    onError: (_err, _id, context) => {
      if (context?.previousList) {
        queryClient.setQueryData(ME_LIST_KEY, context.previousList);
      }
      if (context?.previousInfinite) {
        queryClient.setQueryData(ME_INFINITE_KEY, context.previousInfinite);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ME_INVALIDATE_PREFIX });
      if (spaceId) {
        queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'conversations'] });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Default exports — most callers (operator surfaces, navbar) read cross-space.
// `useConversation(id)` lookups also resolve cross-space (the URL alone
// identifies the conversation; backend resolves the space).
// ---------------------------------------------------------------------------

export const useConversations = useMeConversations;
export const useInfiniteConversations = useInfiniteMeConversations;

/** Legacy create/update/delete — target the user's default (Personal) space.
 *  Console pages that need to write into a specific space should use the
 *  explicit `useCreateConversationIn(spaceId)` family instead. */
export function useCreateConversation() {
  return useCreateConversationIn(useDefaultConversationSpaceId());
}

export function useUpdateConversation() {
  return useUpdateConversationIn(useDefaultConversationSpaceId());
}

export function useTogglePinConversation() {
  return useTogglePinConversationIn(useDefaultConversationSpaceId());
}

export function useDeleteConversation() {
  return useDeleteConversationIn(useDefaultConversationSpaceId());
}
/**
 * Resolve the space a conversation belongs to.
 *
 * Backend rule: every single-entity read goes through
 * `/spaces/{spaceId}/conversations/{id}`. The cross-space `/me/conversations`
 * list returns each row with its own `spaceId`, so this hook checks the
 * already-loaded list cache. If the conversation isn't in the loaded pages
 * (deep-link, or paged past), we fall back to the user's last-visited
 * space, then `defaultSpaceId`. Returns `undefined` if none of those are
 * available — caller should render a loading/empty state.
 */
export function useConversationSpaceId(conversationId: string | undefined): string | undefined {
  const { data } = useMeConversations();
  const { data: infiniteData } = useInfiniteQuery({
    queryKey: ME_INFINITE_KEY,
    queryFn: () => Promise.resolve({ items: [] as Conversation[], nextToken: undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: () => undefined,
    enabled: false,
  });
  const { user } = useAuth();
  if (!conversationId) return undefined;
  const fromList = data?.items.find((c) => c.conversationId === conversationId);
  const fromInfinite = infiniteData?.pages
    .flatMap((p) => p.items)
    .find((c) => c.conversationId === conversationId);
  const cached = fromList ?? fromInfinite;
  const spaceFromCache = (cached as (Conversation & { spaceId?: string }) | undefined)?.spaceId;
  return spaceFromCache ?? user?.preferences?.lastSpaceId ?? user?.defaultSpaceId;
}

/**
 * Single-conversation lookup. Resolves the conversation's space via the
 * cached list (or the user's default), then calls the space-scoped read.
 */
export function useConversation(conversationId: string | undefined) {
  const spaceId = useConversationSpaceId(conversationId);
  return useQuery({
    queryKey: ['space', spaceId, 'conversations', conversationId],
    queryFn: () => spaceApi(spaceId!).conversations.getConversation(conversationId!),
    enabled: !!conversationId && !!spaceId,
  });
}
